import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { DataGridPro } from '@mui/x-data-grid-pro';
import useFetchApi from '../../components/hooks/useFetchApi';
import { formatDate, formatDateTime, getFormatConfig, initializeFormats } from '../../utils/global';
import { harmonateTokens as ht } from '../../theme/harmonateTokens';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateUploadOutlinedSx,
  harmonateToolbarFieldSx,
} from '../../theme/harmonateStyles';

const yesNoChipCell = (params) => {
  const v = params.value ? 'Yes' : 'No';
  const isYes = v === 'Yes';
  return (
    <Chip
      label={v}
      size="small"
      sx={{
        height: 24,
        fontSize: '0.75rem',
        fontWeight: 500,
        bgcolor: isYes ? ht.secondaryTab : ht.listBg,
        color: isYes ? ht.text : ht.text,
        borderRadius: ht.buttonRadius,
        '& .MuiChip-label': { px: 1.5 },
      }}
    />
  );
};

const Document = () => {
  const tenantId = localStorage.getItem('tenant_id');
  const { data: apiData, loading: apiLoading, error, refetch } = useFetchApi({
    endpoint: `conductor/file-instances/all-documents?tenant=${tenantId}`,
    resGetter: (res) => res?.data || [],
    retrieveOnMount: true,
  });

  const [workflowData, setWorkflowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Columns: show only the requested fields (read-only)
  const workflowColumns = [
    { field: 'name', headerName: 'Name', flex: 2 },
    { field: 'documentTypeName', headerName: 'Document Type', flex: 1.5 },
    { field: 'fundName', headerName: 'Fund', flex: 1.5 },
    { field: 'workflowName', headerName: 'Workflow', flex: 1.5 },
    { field: 'username', headerName: 'Uploaded By', flex: 1.5 },
    { 
      field: 'createdAt', 
      headerName: 'Created Date', 
      flex: 1,
      type: 'dateTime',
      renderCell: (params) => {
        const val = params.value;
        if (!val) return '';
        const dtFormat = getFormatConfig()?.DateTimeFormat || getFormatConfig()?.DateFormat || 'DD/MM/YYYY hh:mm:ss a';
        try {
          return formatDateTime(val, dtFormat);
        } catch {
          return String(val);
        }
      }
    },
    {
      field: 'deleted',
      headerName: 'Deleted',
      width: 110,
      renderCell: yesNoChipCell,
    }
  ];

  // Refresh data (refetch)
  const refreshData = async () => {
    setLoading(true);
    try {
      await refetch();
    } catch (e) {
      console.error('Failed to refresh documents:', e);
    } finally {
      setLoading(false);
    }
  };

  // Map API data to grid rows (only include the selected fields)
  useEffect(() => {
    if (!Array.isArray(apiData)) return;
    const rows = apiData.map((item, index) => {
      return {
        id: item.id || item._id || `doc-${index}`,
        name: item.name || '',
        documentTypeName: item.documentTypeName || item.documentType || '' ,
        fundName: item.fundName || item.fundname || '',
        workflowName: item.workflowName || item.workflow || '',
        username: item.username || item.uploadedBy || item.userName || '',
        createdAt: item.createdAt ? new Date(item.createdAt) : null,
        deleted: item.deleted === true || item.deleted === 'true' || item.deleted === 1
      };
    });
    setWorkflowData(rows);
  }, [apiData]);

  // Initialize formats from app config so date/time formats match tenant settings
  useEffect(() => {
    const tId = tenantId;
    if (!tId) return;
    let isMounted = true;
    (async () => {
      try {
        await initializeFormats(tId);
        if (isMounted) {
          // trigger re-render by updating state (noop) if needed
          setWorkflowData(prev => [...prev]);
        }
      } catch (e) {
        console.warn('Failed to initialize formats for Documents page', e);
      }
    })();
    return () => { isMounted = false; };
  }, [tenantId]);

  const filteredRows = React.useMemo(() => {
    if (!searchValue || !searchValue.trim()) return workflowData;
    const q = searchValue.trim().toLowerCase();
    return workflowData.filter(r => {
      const dateFmt = getFormatConfig()?.DateFormat || 'DD/MM/YYYY';
      const dtFmt = getFormatConfig()?.DateTimeFormat || dateFmt || 'DD/MM/YYYY hh:mm:ss a';
      const createdStrDate = r.createdAt ? (formatDate(r.createdAt, dateFmt) || '') : '';
      const createdStrDateTime = r.createdAt ? (formatDateTime(r.createdAt, dtFmt) || '') : '';
      // Deleted can be boolean; expose several textual representations for searching
      const deletedBool = !!r.deleted;
      const deletedStr = deletedBool ? 'yes' : 'no';
      const deletedAltStrs = deletedBool ? ['yes','y','true','1'] : ['no','n','false','0'];
      return (
        (r.name || '').toLowerCase().includes(q) ||
        (r.documentTypeName || '').toLowerCase().includes(q) ||
        (r.fundName || '').toLowerCase().includes(q) ||
        (r.workflowName || '').toLowerCase().includes(q) ||
        (r.username || '').toLowerCase().includes(q) ||
        createdStrDate.toLowerCase().includes(q) ||
        createdStrDateTime.toLowerCase().includes(q) ||
        deletedStr.toLowerCase().includes(q) ||
        deletedAltStrs.includes(q)
      );
    });
  }, [workflowData, searchValue]);

  return (
    <Box sx={{ ...harmonatePageSx, height: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={harmonatePageTitleSx}>
          Documents
        </Typography>
        <Box sx={{ width: 360 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={harmonateToolbarFieldSx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: ht.white }} />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Box>

      {apiLoading || loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress sx={{ color: ht.text }} />
        </Box>
      ) : error || localError ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Alert severity="error">{error?.message || String(localError)}</Alert>
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            ...harmonateCardSx,
            flex: 1,
            minHeight: 0,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DataGridPro
            rows={filteredRows}
            columns={workflowColumns}
            rowHeight={40}
            autoHeight={false}
            columnResizeMode="onEnd"
            resizable
            pagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            pageSize={100}
            autoPageSize={false}
            checkboxSelection={false}
            disableSelectionOnClick
            getRowId={(row) => row.id}
            sx={{
              ...harmonateDataGridSx,
              flex: 1,
              minHeight: 0,
              height: '100%',
              '& .MuiDataGrid-row:hover': {
                bgcolor: ht.secondaryTab,
                cursor: 'default',
              },
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
            }}
            components={{
              NoRowsOverlay: () => (
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography variant="body1" sx={{ mb: 2, color: ht.text }}>
                    No documents available
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={refreshData}
                    sx={harmonateUploadOutlinedSx}
                  >
                    Refresh
                  </Button>
                </Box>
              )
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default Document;
